import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { List } from 'immutable';
import moment from 'moment';

import {
  fetchTask,
  saveTask,
  setTask,
  deleteTask,
} from '../../store/modules/task';
import { Task } from '../Task/Task';
import FormTaskEmbed from '../FormTaskEmbed/FormTaskEmbed';
import ContextMenu from './ContextMenu';
import classes from './TaskList.scss';

require('moment/locale/de');

const initialTask = {
  title: '',
  notes: '',
  jsTime: Date.now(),
  due_date: moment().format('YYYY-MM-DD HH:mm:ss'),
  id: `temp_task_${Date.now()}`,
  assignee: null,
  editing: true,
  priority: 0,
};

export class TaskList extends React.Component {
  constructor(props) {
    super(props);

    const localSettingsSaved = JSON.parse(
      localStorage.getItem('localSettings'),
    );

    this.state = {
      task: {
        ...initialTask,
        assignee: props.assigneeId.length ? props.assigneeId : props.staffId,
        isScrolling: false,
        openEvent: null,
        menuList: null,
        files: [],
        taskOrder:
          localSettingsSaved !== null
            ? localSettingsSaved.taskOrder
            : 'due_date',
      },
    };

    this.scrollTimer = null;
  }

  componentDidMount() {
    this.scrollingContainer = document.querySelector(
      this.props.scrollingContainer,
    );
    if (this.scrollingContainer) {
      // https://stackoverflow.com/questions/38114715/how-to-reveal-a-react-component-on-scroll
      this.scrollingContainer.addEventListener('scroll', this.checkIfScrolling);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const omit = ['saveTask', 'setTask', 'deleteTask'];

    if (
      JSON.stringify(_.omit(nextProps, omit)) !==
      JSON.stringify(_.omit(this.props, omit))
    ) {
      return true;
    }

    if (JSON.stringify(nextState) !== JSON.stringify(this.state)) {
      return true;
    }

    return false;
  }

  componentWillUnmount() {
    if (this.scrollTimer !== null) {
      clearTimeout(this.scrollTimer);
    }

    this.scrollingContainer.removeEventListener(
      'scroll',
      this.checkIfScrolling,
    );
  }

  checkIfScrolling = e => {
    if (this.state.isScrolling === false) {
      this.setState({ isScrolling: true, openEvent: null });
    }

    if (this.scrollTimer !== null) {
      clearTimeout(this.scrollTimer);
    }
    this.scrollTimer = setTimeout(() => {
      // console.log('scrolled');
      this.setState({ isScrolling: false });
    }, 150);
  };

  openContextMenu = (e, items) => {
    e.preventDefault();

    this.setState({
      openEvent: {
        absX: e.clientX,
        absY: e.clientY,
      },
      menuList: items,
    });
  };

  updateTask = task => {
    if (this.state.task.editing === true && task.editing === true) {
      // still editing the task
      this.setState({ task });
    } else {
      // stopped editing, time to save the task
      // console.log('saving!', task);
      this.props.saveTask({
        ...task,
        id: null,
        linkWithId: this.props.taskableId,
        linkWithType: this.props.taskableType,
      });

      this.setState({
        task: {
          ...initialTask,
          assignee: '--clearme--',
        },
      });
    }
  };

  renderTaskForm = () => {
    if (this.props.showTaskForm) {
      return (
        <div className={`form ${classes.form}`}>
          <FormTaskEmbed
            onChange={task => this.updateTask(task)}
            task={this.state.task}
            defaultAssignee={
              this.props.assigneeId.length
                ? this.props.assigneeId
                : this.props.staffId
            }
          />
        </div>
      );
    }
    return null;
  };

  orderByTitle = (a, b) => {
    const o1 = a.is_done;
    const o2 = b.is_done;

    if (o1 < o2) return -1;
    if (o1 > o2) return 1;

    if (this.state.task.taskOrder === 'created_at') {
      // user wants to sort by creation, cant be zero
      const c1 = a.created_at;
      const c2 = b.created_at;

      if (c1 < c2) return -1;
      if (c1 > c2) return 1;
    } else if (this.state.task.taskOrder === 'due_date') {
      // user wants to sort by due date
      const c1 = a.due_date;
      const c2 = b.due_date;

      // both have a due date
      if (c1 !== null && c2 !== null) {
        if (c1 < c2) return -1;
        if (c1 > c2) return 1;
      }

      // one of them does not have a due date, the one with a due date should come first
      if (c2 === null && c1 !== null) return -1;
      if (c2 !== null && c1 === null) return 1;
    }

    // results are equal so far, lets compare titles
    return a.title.localeCompare(b.title);
  };

  renderTasks = () => {
    if (
      this.props.tasks === 'undefined' ||
      !Array.isArray(this.props.tasks) ||
      this.props.tasks.length === 0
    ) {
      return (
        <div>
          <p className="text-center">
            <small>Keine Aufgaben eingetragen.</small>
          </p>
        </div>
      );
    }

    const today = moment().startOf('day');

    if (typeof this.props.tasks.sort === 'function') {
      const tasks = List(this.props.tasks);

      return tasks
        .filter(task => {
          if (task.deleted_at !== null) {
            return false;
          }

          if (task.due_date === null && this.props.showNullDueDates === false) {
            // has a null date but null dates shouldn't show
            return false;
          } else if (
            task.due_date !== null &&
            !today.isSameOrAfter(task.due_date, 'day')
          ) {
            // careful, task is in future, are future tasks allowed?
            if (this.props.showFutureDates === false) {
              // no they're not
              return false;
            }
          }

          return true;
        })
        .sort(this.orderByTitle)
        .map(task => (
          <Task
            task={{ ...task }}
            key={`task_${task.id}`}
            setTask={this.props.setTask}
            saveTask={this.props.saveTask}
            deleteTask={this.props.deleteTask}
            openContextMenu={this.openContextMenu}
            closeContextMenu={() => this.setState({ openEvent: false })}
          />
        ));
    }

    return (
      <div>
        <p className="text-center">
          <small>Konnte keine Aufgaben laden.</small>
        </p>
      </div>
    );
  };

  render() {
    return (
      <div>
        {this.renderTasks()}
        {this.renderTaskForm()}
        <ContextMenu
          menuList={this.state.menuList}
          openEvent={this.state.openEvent}
          onClose={() => this.setState({ openEvent: null })}
        />
      </div>
    );
  }
}

TaskList.propTypes = {
  tasks: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  showTaskForm: PropTypes.bool,
  taskableType: PropTypes.string,
  taskableId: PropTypes.string,
  staffId: PropTypes.string,
  assigneeId: PropTypes.string,
  saveTask: PropTypes.func,
  setTask: PropTypes.func,
  deleteTask: PropTypes.func,
  scrollingContainer: PropTypes.string.isRequired,
  showNullDueDates: PropTypes.bool,
  showFutureDates: PropTypes.bool,
};

TaskList.defaultProps = {
  tasks: [],
  showTaskForm: false,
  staffId: '',
  assigneeId: '',
  taskableType: '',
  taskableId: '',
  scrollingContainer: '#scroller',
  showNullDueDates: true,
  showFutureDates: false,
};

const mapActionCreators = {
  fetchTask,
  saveTask,
  deleteTask,
  setTask,
};

const mapStateToProps = (state, props) => {
  let staffId = '';

  if (typeof state.login !== 'undefined') {
    staffId = state.login.staff.id;
  }

  return {
    staffId,
  };
};

export default connect(mapStateToProps, mapActionCreators)(TaskList);



// WEBPACK FOOTER //
// ./src/components/TaskList/index.js